import React, { useEffect, Children } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Heart, Target, Trophy, Users, Brain, Shield, Smile, TrendingUp, Globe, UserCheck, Dumbbell, Zap, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
export function ChildBenefitsPage() {
  useEffect(() => {
    document.title = "Benefits for Children | Walker's Martial Arts";
  }, []);
  const benefits = [{
    icon: Dumbbell,
    title: 'Physical Fitness',
    items: [{
      subtitle: 'Strength & Flexibility',
      description: 'Martial arts involve movements that help build strength, flexibility, and stamina. Training often includes exercises that promote muscle development, coordination, and balance.'
    }, {
      subtitle: 'Improved Motor Skills',
      description: 'Through techniques like kicking, punching, and blocking, children refine their fine motor coordination and improve skills.'
    }, {
      subtitle: 'Cardiovascular Health',
      description: 'Regular training sessions are aerobic and help children develop cardiovascular endurance and overall body awareness.'
    }]
  }, {
    icon: Target,
    title: 'Discipline & Self-Control',
    items: [{
      subtitle: 'Respect for Authority',
      description: 'Martial arts often emphasize respect for instructors, peers, and oneself, teaching children how to behave and work in structured environments.'
    }, {
      subtitle: 'Self-Discipline',
      description: 'The structured nature and method of training, children learn to stay focused and disciplined, which can translate to other areas of their lives (homework, household responsibilities, etc.).'
    }, {
      subtitle: 'Emotional Regulation',
      description: 'Training teaches children to manage their feelings of nervousness, frustration, anger, and other strong emotions, enhancing self-control and composure.'
    }]
  }, {
    icon: Trophy,
    title: 'Confidence & Self-Esteem',
    items: [{
      subtitle: 'Achievement & Progress',
      description: 'Earning belts or mastering techniques provides a tangible sense of accomplishment, boosting self-confidence.'
    }, {
      subtitle: 'Overcoming Challenges',
      description: 'Martial arts training requires persistence and mental toughness. Facing children face challenges in a supportive environment.'
    }]
  }, {
    icon: Users,
    title: 'Social Skills',
    items: [{
      subtitle: 'Teamwork',
      description: 'Though martial arts can be performed, many practices encourage group activities, teaching of "show how to collaborate and communicate effectively."'
    }, {
      subtitle: 'Building Friendships',
      description: 'Children who train together often form strong friendships, creating a sense of community and support.'
    }, {
      subtitle: 'Respect for Others',
      description: 'Karate arts teaches humility and respect, both for oneself and for others, promoting positive social interactions.'
    }]
  }, {
    icon: Brain,
    title: 'Focus & Concentration',
    items: [{
      subtitle: 'Mental Clarity',
      description: 'Martial arts require a high level of focus to execute moves correctly. This helps children improve their attention span and ability to concentrate, which can benefit their academic performance.'
    }, {
      subtitle: 'Problem-Solving Skills',
      description: 'Many martial arts, including Brazilian Jiu-Jitsu, involve strategy, helping children develop critical thinking and decision-making skills.'
    }]
  }, {
    icon: Shield,
    title: 'Safety Awareness & Self-Defense',
    items: [{
      subtitle: 'Practical Skills',
      description: 'Martial arts teach children basic self-defense techniques, giving them the tools to protect themselves if needed.'
    }, {
      subtitle: 'Increased Awareness',
      description: "Training also promotes awareness of one's surroundings, which can help prevent dangerous situations."
    }]
  }, {
    icon: Smile,
    title: 'Stress Relief & Emotional Balance',
    items: [{
      subtitle: 'Physical Outlet',
      description: 'Martial arts provide a constructive way for children to channel energy and stress in a healthy, controlled environment.'
    }, {
      subtitle: 'Mindfulness & Focus',
      description: 'Many martial arts disciplines, like karate or tai chi, incorporate elements of mindfulness, promote an emotional resilience.'
    }]
  }, {
    icon: TrendingUp,
    title: 'Goal Setting & Motivation',
    items: [{
      subtitle: 'Structured Progression',
      description: 'The belt system or similar rank gives children a clear framework for setting and achieving goals, which can boost motivation.'
    }, {
      subtitle: 'Motivation to Succeed',
      description: 'As children progress through the ranks, they gain a sense of accomplishment that drives them to keep working hard.'
    }]
  }, {
    icon: Globe,
    title: 'Cultural Awareness',
    items: [{
      subtitle: 'Exposure to Different Traditions',
      description: 'Many martial arts have rich histories and traditions from different cultures. This can help children develop an appreciation for diversity and learn about other cultures.'
    }]
  }, {
    icon: UserCheck,
    title: 'Prevention of Bullying',
    items: [{
      subtitle: 'Increased Confidence',
      description: 'Children who are confident in their skills and abilities are often less likely to be targeted by bullies.'
    }, {
      subtitle: 'Calmness Under Pressure',
      description: 'Martial arts training teaches children to remain calm and composed, which can deter bullying situations or help them handle conflict without escalation.'
    }]
  }];
  return <div className="bg-black min-h-screen" data-id="element-513">
      <Navigation data-id="element-514" />

      {/* Hero Section */}
      <div className="relative pt-20 pb-16 overflow-hidden" data-id="element-515">
        <div className="absolute inset-0 z-0" data-id="element-516">
          <img src="https://cdn.magicpatterns.com/uploads/42LHKyKLSTkPyhCS9FXYGj/childbenefits.png" alt="Child Benefits" className="w-full h-full object-cover opacity-20" data-id="element-517" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" data-id="element-518" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16" data-id="element-519">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-520">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-521">
              Benefits for <span className="text-[#00D4FF]" data-id="element-522">Children</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed" data-id="element-523">
              Walker's Martial Arts offers numerous benefits for children,
              helping to develop both physical and mental skills that contribute
              to their overall well-being. Some of the key advantages include:
            </p>
          </motion.div>
        </div>
      </div>

      {/* Benefits Sections */}
      <section className="py-20 bg-[#0a0a0a]" data-id="element-524">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-525">
          <div className="space-y-24" data-id="element-526">
            {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className="relative" data-id="element-527">
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-8" data-id="element-528">
                  <div className="flex items-center justify-center w-14 h-14 bg-[#00D4FF]/10 rounded-2xl border border-[#00D4FF]/30" data-id="element-529">
                    <benefit.icon className="w-7 h-7 text-[#00D4FF]" data-id="element-530" />
                  </div>
                  <div data-id="element-531">
                    <span className="text-[#00D4FF] font-bold text-lg" data-id="element-532">
                      {index + 1}.
                    </span>
                    <h2 className="text-3xl font-bold text-white inline ml-3" data-id="element-533">
                      {benefit.title}
                    </h2>
                  </div>
                </div>

                {/* Items */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ml-0 md:ml-18" data-id="element-534">
                  {benefit.items.map((item, itemIndex) => <motion.div key={item.subtitle} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: itemIndex * 0.1
              }} className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-800 hover:border-[#00D4FF]/30 transition-colors" data-id="element-535">
                      <h3 className="text-lg font-bold text-white mb-3 flex items-start gap-2" data-id="element-536">
                        <Zap className="w-5 h-5 text-[#00D4FF] flex-shrink-0 mt-0.5" data-id="element-537" />
                        {item.subtitle}
                      </h3>
                      <p className="text-gray-400 leading-relaxed text-sm" data-id="element-538">
                        {item.description}
                      </p>
                    </motion.div>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Summary Section */}
      <section className="py-20 bg-black" data-id="element-539">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-540">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-541">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00D4FF]/10 rounded-full mb-6" data-id="element-542">
              <Heart className="w-8 h-8 text-[#00D4FF]" data-id="element-543" />
            </div>
            <p className="text-2xl text-gray-300 leading-relaxed mb-12" data-id="element-544">
              Overall, Walker's Martial Arts can provide a holistic approach to
              a child's development, blending physical activity with valuable
              life lessons in respect, self-discipline, and personal growth.
            </p>

            <div className="bg-gradient-to-r from-[#00D4FF]/10 to-transparent p-8 rounded-2xl border border-[#00D4FF]/20 mb-12" data-id="element-545">
              <p className="text-xl text-white font-semibold mb-6" data-id="element-546">
                We invite you to try a complimentary class
              </p>
              <a href="#schedule" className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-bold rounded-full hover:bg-[#00D4FF]/90 transition-colors" data-id="element-547">
                <Activity className="w-5 h-5" data-id="element-548" />
                Schedule Free Trial
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-549" />
    </div>;
}